import React, { Dispatch, ReactElement, ReactNode } from "react";
import { Item } from "./Item";
import { Transition } from "react-transition-group";
import { useDispatch } from "react-redux";
import { AlternateMenuItemOnClick } from "components/Category/CrudMenu";
import { StringLiteral } from "@babel/types";
import closeIcon from "assets/Icon_Modal_Cross.png";

export type RightMenuItem = {
  icon: string;
  name: string;
  imgAlt: string;
  disabled?: boolean;
  onClick(id?: React.ReactText | null): void;
  notInMenus?: any;
  alternateMenuItemOnClick?: AlternateMenuItemOnClick;
  permissionKey: string | undefined;
  customItem?: (props?: any) => JSX.Element;
};

export type AlternateRightMenuItems = {
  menuItems: RightMenuItem[];
};

export type RightMenuItems = {
  menuItems: RightMenuItem[];
  alternateMenuItems: AlternateRightMenuItems;
};

type RightMenuProps = {
  visible: boolean;
  menuItems: RightMenuItem[];
  alternateMenuItems: AlternateRightMenuItems;
  toggleAltMenuItems: boolean;
  selectedDataIDs: number[] | null;
  additionalDataIDs?: any;
  parentSelectedIdName?: string;
  parentSelectedId?: number;
  onRightMenuClose(): void;
};

type Props = RightMenuProps;

interface MenuItemsProps {
  toggleAltMenuItems: boolean;
  alternateMenuItems: AlternateRightMenuItems;
  menuItems: RightMenuItem[];
  selectedDataIDs: number[] | null;
  additionalDataIDs?: any;
  position: "RightMenu" | "TableRowOption";
  parentSelectedIdName?: string;
  parentSelectedId?: number;
}

const MenuItems: React.FC<MenuItemsProps> = ({
  toggleAltMenuItems,
  alternateMenuItems,
  menuItems,
  selectedDataIDs,
  additionalDataIDs,
  position,
  parentSelectedIdName,
  parentSelectedId,
}) => {
  const dispatch = useDispatch();

  if (toggleAltMenuItems) {
    return (
      <>
        {alternateMenuItems.menuItems
          .filter(m => !m.notInMenus)
          .map((item, key) => (
            <Item
              key={key}
              onClick={
                item.alternateMenuItemOnClick
                  ? () =>
                      item.alternateMenuItemOnClick?.(
                        dispatch,
                        selectedDataIDs,
                        additionalDataIDs
                      )
                  : item.onClick
              }
              icon={item.icon}
              name={item.name}
              imgAlt={item.imgAlt}
              additionalDataIDs={additionalDataIDs}
              position={position}
              customItem={item.customItem}
              dispatch={dispatch}
              disabled={item.disabled}
              permissionKey={item.permissionKey}
            />
          ))}
      </>
    );
  } else {
    return (
      <>
        {menuItems
          .filter(m => !m.notInMenus)
          .map((item, key) => {
            let handleOnClick = item.onClick;

            if (item.alternateMenuItemOnClick) {
              handleOnClick = () =>
                item.alternateMenuItemOnClick?.(
                  dispatch,
                  selectedDataIDs,
                  additionalDataIDs
                );
            }

            return (
              <Item
                key={key}
                onClick={handleOnClick}
                icon={item.icon}
                name={item.name}
                imgAlt={item.imgAlt}
                disabled={item.disabled}
                customItem={item.customItem}
                additionalDataIDs={additionalDataIDs}
                position={position}
                dispatch={dispatch}
                permissionKey={item.permissionKey}
              />
            );
          })}
      </>
    );
  }
};

const RightMenu: React.FC<Props> = ({
  visible,
  onRightMenuClose,
  ...menuItemsProps
}) => {
  return (
    <Transition in={visible} timeout={0} mountOnEnter>
      {transitionState => (
        <div className={"RightMenu " + transitionState}>
          <div
            className="CloseMenu"
            onClick={e => {
              e.stopPropagation();
              onRightMenuClose();
            }}
          >
            <img src={closeIcon} alt="close menu" className="CloseIcon" />
          </div>
          <div className="ItemsContainer">
            <MenuItems position={"RightMenu"} {...menuItemsProps} />
          </div>
        </div>
      )}
    </Transition>
  );
};

export { RightMenu };
